/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import _ from 'lodash';

import type { IntegrationSchema } from '@ibm-watson/shared/ts/integration';
import { FIELDS } from './schema';

/**
 * Complete regular expression for the deployment code.
 * Capture group 2 contains the salesforce domain string.
 * Capture group 3 contains the deployment account ID string.
 * Capture group 4 contains the organization ID string.
 */
export const deploymentCodeRegex =
  /^<script.*>(.|\n)*liveagent.init\('https:\/\/(.*)\/.*',\s?'(.*)',\s?'(.*)'\)(.|\n)*<\/script>\n*$/;

export const getSfDomain = (deploymentCode: string): string | null =>
  deploymentCodeRegex.exec(deploymentCode.trim())?.[2];

export const getDeploymentId = (deploymentCode: string): string | null =>
  deploymentCodeRegex.exec(deploymentCode.trim())?.[3];

export const getOrganizationId = (deploymentCode: string): string | null =>
  deploymentCodeRegex.exec(deploymentCode.trim())?.[4];

/**
 * Complete regular expression for the button code.
 * Capture group 3 contains the button ID string.
 */
export const buttonCodeRegex =
  /^(.|\n)*<script.*>(.|\n)*liveagent\.showWhenOffline\('(\w*)',.*\)(.|\n)*<\/script>\n*$/;

export const getButtonId = (buttonCode: string): string | null =>
  buttonCodeRegex.exec(buttonCode.trim())?.[3];

/**
 *
 */
export const safeEncode = (value: string): string => {
  const decoded = decodeURIComponent(value);
  return value === decoded ? encodeURIComponent(value) : value;
};

/**
 *
 */
export const encodeSalesforceSettings = (
  integration: IntegrationSchema
): IntegrationSchema => {
  const settings = _.cloneDeep(integration.settings);

  const deploymentCode = safeEncode(
    _.get(settings, FIELDS.ORGANIZATION_DEPLOYMENT_CODE)
  );
  _.set(settings, FIELDS.ORGANIZATION_DEPLOYMENT_CODE, deploymentCode);

  const buttonCode = safeEncode(_.get(settings, FIELDS.BUTTON_CODE));
  _.set(settings, FIELDS.BUTTON_CODE, buttonCode);

  return {
    ...integration,
    settings
  };
};
